import React from 'react';

const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const TypeConfigPanel = ({ eventType, configuration = {}, onChange }) => {
  const set = (key, value) => onChange({ ...configuration, [key]: value });

  if (eventType === 'ATTENDANCE') {
    return (
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Attendance settings</h4>
        <label className={labelClass}>
          Mode
          <select value={configuration.mode || 'YES_NO'} onChange={(e) => set('mode', e.target.value)} className={inputClass}>
            <option value="YES_NO">Yes/No attendance</option>
            <option value="CHECK_IN_OUT">Check-in / Check-out</option>
            <option value="QR">QR Check-in</option>
            <option value="MANUAL">Manual attendance</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className={labelClass}>
            Grace period (min)
            <input type="number" min="0" value={configuration.gracePeriodMinutes ?? 15} onChange={(e) => set('gracePeriodMinutes', Number(e.target.value))} className={inputClass} />
          </label>
          <label className={labelClass}>
            Late threshold (min)
            <input type="number" min="0" value={configuration.lateThresholdMinutes ?? 15} onChange={(e) => set('lateThresholdMinutes', Number(e.target.value))} className={inputClass} />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={configuration.trackPercentage !== false} onChange={(e) => set('trackPercentage', e.target.checked)} className="rounded" />
          Track attendance percentage
        </label>
      </div>
    );
  }

  if (eventType === 'SPLITWISE') {
    const billImage = {
      enabled: true,
      required: false,
      ...(configuration.fields?.image || {}),
    };
    const setBillImage = (patch) => {
      const nextImage = { ...billImage, ...patch };
      if (patch.enabled === false) nextImage.required = false;
      onChange({
        ...configuration,
        fields: {
          ...(configuration.fields || {}),
          image: nextImage,
        },
      });
    };
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200">Expense sharing</h4>
        <div className="grid grid-cols-2 gap-3">
          <label className={labelClass}>
            Currency
            <select value={configuration.currency || 'INR'} onChange={(e) => set('currency', e.target.value)} className={inputClass}>
              <option value="INR">INR ₹</option>
              <option value="USD">USD $</option>
              <option value="EUR">EUR €</option>
            </select>
          </label>
          <label className={labelClass}>
            Split method
            <select value={configuration.splitMethod || 'EQUAL'} onChange={(e) => set('splitMethod', e.target.value)} className={inputClass}>
              <option value="EQUAL">Equal</option>
              <option value="PERCENTAGE">Percentage</option>
              <option value="EXACT">Exact amount</option>
              <option value="SHARES">Shares</option>
              <option value="CUSTOM">Custom</option>
            </select>
          </label>
        </div>
        <label className={labelClass}>
          Who can add expenses?
          <select value={configuration.whoCanAddExpenses || 'ALL'} onChange={(e) => set('whoCanAddExpenses', e.target.value)} className={inputClass}>
            <option value="ALL">All participants</option>
            <option value="CREATOR">Creator only</option>
            <option value="ADMINS">Admins only</option>
          </select>
        </label>
        <div className="rounded-lg border border-amber-100 dark:border-amber-900/50 bg-white/70 dark:bg-gray-900/30 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 min-w-0">
              <input
                type="checkbox"
                checked={billImage.enabled !== false}
                onChange={(e) => setBillImage({ enabled: e.target.checked })}
                className="rounded"
              />
              <span className="font-medium">Bill image</span>
            </label>
            <label className={`flex items-center gap-1.5 text-xs font-semibold ${billImage.enabled === false ? 'text-gray-400' : 'text-amber-700 dark:text-amber-300'}`}>
              <input
                type="checkbox"
                disabled={billImage.enabled === false}
                checked={billImage.enabled !== false && billImage.required === true}
                onChange={(e) => setBillImage({ required: e.target.checked })}
                className="rounded"
              />
              Required
            </label>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 pl-6">
            People can attach a receipt or bill photo with each shared expense.
          </p>
        </div>
      </div>
    );
  }

  if (eventType === 'EXPENSE_TRACKING') {
    const fields = {
      item: { enabled: true, required: true },
      itemCategory: { enabled: true, required: false },
      amount: { enabled: true, required: true },
      boughtPlace: { enabled: true, required: false },
      paymentStatus: { enabled: true, required: false },
      image: { enabled: true, required: false },
      ...(configuration.fields || {}),
    };
    const fieldDefs = [
      { id: 'item', label: 'Item' },
      { id: 'itemCategory', label: 'Item category' },
      { id: 'amount', label: 'Amount' },
      { id: 'boughtPlace', label: 'Bought place' },
      { id: 'paymentStatus', label: 'Amount paid / Udhaar' },
      { id: 'image', label: 'Bill image' },
    ];
    const setField = (id, patch) => {
      const next = { ...fields, [id]: { ...fields[id], ...patch } };
      if (patch.enabled === false) next[id].required = false;
      onChange({ ...configuration, currency: configuration.currency || 'INR', fields: next, whoCanAddExpenses: configuration.whoCanAddExpenses || 'ALL' });
    };
    return (
      <div className="rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-200">Expense tracking fields</h4>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Choose which fields people fill when they add an entry. Tick Required for fields that must be filled.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className={labelClass}>
            Currency
            <select value={configuration.currency || 'INR'} onChange={(e) => set('currency', e.target.value)} className={inputClass}>
              <option value="INR">INR ₹</option>
              <option value="USD">USD $</option>
              <option value="EUR">EUR €</option>
            </select>
          </label>
          <label className={labelClass}>
            Who can add entries?
            <select value={configuration.whoCanAddExpenses || 'ALL'} onChange={(e) => set('whoCanAddExpenses', e.target.value)} className={inputClass}>
              <option value="ALL">Everyone in the channel</option>
              <option value="CREATOR">Creator only</option>
            </select>
          </label>
        </div>
        <div className="rounded-lg border border-orange-100 dark:border-orange-900/50 divide-y divide-orange-100 dark:divide-orange-900/50 bg-white/70 dark:bg-gray-900/30">
          {fieldDefs.map((field) => {
            const state = fields[field.id] || { enabled: true, required: false };
            return (
              <div key={field.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 min-w-0">
                  <input
                    type="checkbox"
                    checked={state.enabled !== false}
                    onChange={(e) => setField(field.id, { enabled: e.target.checked })}
                    className="rounded"
                  />
                  <span className="font-medium truncate">{field.label}</span>
                </label>
                <label className={`flex items-center gap-1.5 text-xs font-semibold ${state.enabled === false ? 'text-gray-400' : 'text-orange-700 dark:text-orange-300'}`}>
                  <input
                    type="checkbox"
                    disabled={state.enabled === false}
                    checked={state.enabled !== false && state.required === true}
                    onChange={(e) => setField(field.id, { required: e.target.checked })}
                    className="rounded"
                  />
                  Required
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (eventType === 'CALCULATION') {
    const inputs = configuration.inputs || [{ name: 'Salary', value: '' }, { name: 'Bonus %', value: '' }];
    return (
      <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-violet-800 dark:text-violet-200">Calculation</h4>
        <label className={labelClass}>
          Formula
          <input type="text" value={configuration.formula || 'Salary * BonusPercentage / 100'} onChange={(e) => set('formula', e.target.value)} className={inputClass} placeholder="e.g. Salary * BonusPercentage / 100" />
        </label>
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Inputs</p>
          {inputs.map((inp, i) => (
            <div key={i} className="grid grid-cols-2 gap-2">
              <input type="text" value={inp.name} onChange={(e) => {
                const next = [...inputs];
                next[i] = { ...next[i], name: e.target.value };
                set('inputs', next);
              }} className={inputClass} placeholder="Name" />
              <input type="text" value={inp.value} onChange={(e) => {
                const next = [...inputs];
                next[i] = { ...next[i], value: e.target.value };
                set('inputs', next);
              }} className={inputClass} placeholder="Value" />
            </div>
          ))}
          <button type="button" onClick={() => set('inputs', [...inputs, { name: '', value: '' }])} className="text-xs text-primary-600 font-semibold">+ Add input</button>
        </div>
      </div>
    );
  }

  if (eventType === 'POLL') {
    const options = configuration.options || ['Option 1', 'Option 2'];
    return (
      <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200">Poll settings</h4>
        <label className={labelClass}>
          Question
          <input type="text" value={configuration.question || ''} onChange={(e) => set('question', e.target.value)} className={inputClass} placeholder="Which restaurant should we choose?" />
        </label>
        {options.map((opt, i) => (
          <input key={i} type="text" value={opt} onChange={(e) => {
            const next = [...options];
            next[i] = e.target.value;
            set('options', next);
          }} className={inputClass} placeholder={`Option ${i + 1}`} />
        ))}
        <button type="button" onClick={() => set('options', [...options, `Option ${options.length + 1}`])} className="text-xs text-primary-600 font-semibold">+ Add option</button>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={configuration.allowMultiple === true} onChange={(e) => set('allowMultiple', e.target.checked)} className="rounded" /> Multiple selection</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={configuration.anonymous === true} onChange={(e) => set('anonymous', e.target.checked)} className="rounded" /> Anonymous</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={configuration.allowVoteChange !== false} onChange={(e) => set('allowVoteChange', e.target.checked)} className="rounded" /> Allow vote changes</label>
        </div>
      </div>
    );
  }

  if (eventType === 'MEETING') {
    return (
      <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50/50 dark:bg-sky-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-sky-800 dark:text-sky-200">Meeting settings</h4>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={configuration.autoRemind === true} onChange={(e) => set('autoRemind', e.target.checked)} className="rounded" /> Auto-remind non-responders</label>
      </div>
    );
  }

  if (eventType === 'TASK') {
    return (
      <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">Task event</h4>
        <label className={labelClass}>
          Default task title
          <input type="text" value={configuration.taskTitle || ''} onChange={(e) => set('taskTitle', e.target.value)} className={inputClass} />
        </label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={configuration.createOnAccept === true} onChange={(e) => set('createOnAccept', e.target.checked)} className="rounded" /> Create task when participant accepts</label>
      </div>
    );
  }

  return null;
};

export default TypeConfigPanel;
